import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import {
  endOfMonth,
  format as formatDate,
  isSameDay,
  isSameYear,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';
import type { MouseEvent, ReactNode, SyntheticEvent } from 'react';
import { forwardRef, useEffect, useId, useState } from 'react';
import { dateFormats } from '../../dateFormats';
import { intent, radius, shadowTokens, sizes, spacingTokens, surface } from '../../theme/tokens';
import { DateCalendar } from '../Calendar';
import { MenuItem, MenuList } from '../Menu';
import { isBeforeDay } from './dayComparison';
import { FilterRangeDay, FilterRangeProvider } from './FilterRangeDay';
import type { FilterTriggerProps } from './FilterTrigger';
import { FilterTrigger } from './FilterTrigger';

/** A date range as `[from, to]`. Half-open (`to` still `null`) only ever exists mid-pick. */
export type FilterDateRange = [Date | null, Date | null];

/** A native `Date` in `mode="single"`, `FilterDateRange` in `mode="range"`, `null` when unset. */
export type FilterDateValue = Date | FilterDateRange | null;

/** A one-click shortcut in the side column, e.g. "Last 30 days". */
export interface FilterDatePreset {
  label: string;
  /**
   * Evaluated when the preset is clicked (and on every render, to highlight the matching row) —
   * NOT at module load, so "today" stays today in a long-lived tab.
   */
  getValue: () => FilterDateValue;
}

const startOfToday = () => startOfDay(new Date());

// The system-wide display masks (src/dateFormats.ts) — month as a 3-letter name, never digits.
// Imported rather than restated so a filter can't drift from the rest of the system.
const DEFAULT_FORMAT = dateFormats.full;
const DEFAULT_FORMAT_WITHOUT_YEAR = dateFormats.dayMonth;
const DEFAULT_RANGE_SEPARATOR = '–';

export interface FormatFilterDateRangeOptions {
  /** date-fns mask for a date that has to carry its own year. Default `'MMM d, yyyy'`. */
  format?: string;
  /** date-fns mask for a date whose year is carried by the other end. Default `'MMM d'`. */
  formatWithoutYear?: string;
  /** Printed between the two dates. Default an en dash. */
  separator?: string;
}

/**
 * Format a date range the way it reads best: **the year is printed once when both ends share it.**
 *
 * - same year → `May 5 – May 7, 2026`
 * - crossing years → `May 5, 2025 – May 7, 2026`
 * - both ends the same day → `May 5, 2026` (a one-day range is a date, and "May 5 – May 5, 2026"
 *   only makes the reader check twice)
 *
 * Exported because the trigger and its tooltip must agree, and because a screen showing the same
 * range outside a filter should print it identically.
 */
export function formatFilterDateRange(
  from: Date,
  to: Date,
  {
    format = DEFAULT_FORMAT,
    formatWithoutYear = DEFAULT_FORMAT_WITHOUT_YEAR,
    separator = DEFAULT_RANGE_SEPARATOR,
  }: FormatFilterDateRangeOptions = {}
): string {
  if (isSameDay(from, to)) return formatDate(to, format);
  // Only the FIRST end drops its year — the year always lands at the end of the string, where it
  // reads as belonging to the whole range.
  const fromMask = isSameYear(from, to) ? formatWithoutYear : format;
  return `${formatDate(from, fromMask)} ${separator} ${formatDate(to, format)}`;
}

/**
 * The default `mode="range"` shortcuts. "Last N days" is **inclusive of today** (Last 7 days =
 * today and the six days before it) — the usual analytics reading.
 */
export const filterDateRangePresets: FilterDatePreset[] = [
  { label: 'Today', getValue: () => [startOfToday(), startOfToday()] },
  {
    label: 'Yesterday',
    getValue: () => [subDays(startOfToday(), 1), subDays(startOfToday(), 1)],
  },
  { label: 'Last 7 days', getValue: () => [subDays(startOfToday(), 6), startOfToday()] },
  { label: 'Last 30 days', getValue: () => [subDays(startOfToday(), 29), startOfToday()] },
  { label: 'Last 90 days', getValue: () => [subDays(startOfToday(), 89), startOfToday()] },
  {
    label: 'This month',
    getValue: () => [startOfMonth(startOfToday()), startOfDay(endOfMonth(startOfToday()))],
  },
  {
    label: 'Last month',
    getValue: () => {
      const prev = subMonths(startOfToday(), 1);
      return [startOfMonth(prev), startOfDay(endOfMonth(prev))];
    },
  },
];

// Layout params of this panel, not design tokens (no width token covers a calendar grid).
// 320 is MUI's own DateCalendar width; the side column is sized to hold "Last 90 days". tokens-ok
const CALENDAR_WIDTH = 320;
const SIDE_COLUMN_WIDTH = 168;

// Styled onto the DEFAULT Popover paper via `slotProps.paper` — never a swapped-in `slots.paper`
// (that breaks Popover's positioning ref; see DECISIONS 2026-07-13). Global MuiPaper untouched.
const paperSx = {
  display: 'flex',
  overflow: 'hidden',
  backgroundColor: surface.white,
  border: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
  borderRadius: `${radius.sm}px`,
  boxShadow: shadowTokens.elevation8,
} as const;

const StyledSideColumn = styled('div')({
  flex: '0 0 auto',
  width: SIDE_COLUMN_WIDTH,
  borderRight: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
  overflowY: 'auto',
});

const StyledCalendarColumn = styled('div')({
  flex: '0 0 auto',
  width: CALENDAR_WIDTH,
});

// Off-screen instructions for the two-click range gesture, which nothing in the DOM otherwise
// conveys. Referenced by the dialog's `aria-describedby`, so it is read once on open.
const StyledVisuallyHidden = styled('p')({
  position: 'absolute',
  // The 1×1 clip rect is the standard sr-only recipe, not a design measurement — same values as
  // FilterBadge's screen-reader suffix. tokens-ok
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  margin: 0,
});

export interface FilterDateButtonProps
  extends Omit<FilterTriggerProps, 'value' | 'valueTooltip' | 'open' | 'onChange' | 'defaultValue'> {
  /** Trigger label — the filter's name, e.g. "Period". */
  label: ReactNode;
  /** One date (`'single'`, default) or an interval (`'range'`). */
  mode?: 'single' | 'range';
  /** Controlled value: a `Date` in single mode, `[from, to]` in range mode, `null` when unset. */
  value?: FilterDateValue;
  /** Uncontrolled initial value. */
  defaultValue?: FilterDateValue;
  /**
   * Called when the value is committed. MUI convention: `(event, value)` — but the event is
   * `null` when the commit came from a calendar day, because MUI X's day callbacks
   * (`onDaySelect` / `DateCalendar.onChange`) hand over the date without the DOM event.
   */
  onChange?: (event: SyntheticEvent | null, value: FilterDateValue) => void;
  /** date-fns mask for a date carrying its own year. Default `'MMM d, yyyy'` (e.g. May 5, 2026). */
  format?: string;
  /**
   * date-fns mask for the start of a range whose year is already carried by its end.
   * Default `'MMM d'`, giving "May 5 – May 7, 2026".
   */
  formatWithoutYear?: string;
  /** Printed between the two dates of a range. Default an en dash. */
  rangeSeparator?: string;
  /**
   * Side-column shortcuts. Defaults to {@link filterDateRangePresets} in `mode="range"` and to
   * none in single mode. Pass `[]` to hide them.
   */
  presets?: FilterDatePreset[];
  /** Offer the reset row at the top of the side column (default true). */
  clearable?: boolean;
  /** Copy for the reset row. Default "All dates". */
  clearLabel?: ReactNode;
  /** Calendar constraints, forwarded to `DateCalendar`. */
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
  shouldDisableDate?: (day: Date) => boolean;
  /** Called when the panel opens / closes (the open state is managed internally). */
  onOpen?: () => void;
  onClose?: () => void;
}

const asRange = (value: FilterDateValue): FilterDateRange => (Array.isArray(value) ? value : [null, null]);

const asSingle = (value: FilterDateValue): Date | null => (Array.isArray(value) ? value[0] : value);

const sameDay = (a: Date | null, b: Date | null) => (a == null || b == null ? a === b : isSameDay(a, b));

/**
 * FilterDateButton — a filter-bar trigger for **one date or one date range**.
 *
 * **The exception to the family's display rule:** everywhere else two applied values collapse to a
 * count badge, but a range is one idea expressed with two dates, and "2" tells the reader nothing.
 * So this trigger always names its value — and prints it the way it reads best, with the year
 * stated once when both ends share it ("May 5 – May 7, 2026" vs "May 5, 2025 – May 7, 2026").
 * See {@link formatFilterDateRange}.
 *
 * The panel is a side column of one-click presets next to a calendar. Presets commit immediately;
 * in `mode="range"` the calendar takes two clicks (first edge, then second — either direction),
 * with the interval previewed under the pointer or the focused cell in between. Closing before the
 * second click discards the half-pick, so the applied value never goes half-open.
 *
 * **HARD RULE 9 — free MUI only.** MUI X's range machinery (`DateRangePicker`,
 * `DateRangeCalendar`, `SingleInputDateRangeField`) is **Pro** (paid), and in a wrapper library a
 * Pro dependency would force every consuming developer to buy a licence. So the range is built on
 * the Community `DateCalendar` plus a custom `slots.day` (`FilterRangeDay`) that paints the band.
 * Designer-approved 2026-08-26 — see docs/DECISIONS.md.
 *
 * Composition — SHARED: `Button`→`ButtonBase` + `Tooltip`→`Popper` (via `FilterTrigger`),
 * `Popover`→`Paper`/`Modal` (styled through this instance's `paper` slot only).
 * LOCAL: `FilterTrigger`, `FilterRangeDay`. Reused: `DateCalendar`, `MenuList` + `MenuItem`.
 *
 * Requires a `LocalizationProvider` (date-fns adapter) above it, like every picker here.
 */
export const FilterDateButton = forwardRef<HTMLButtonElement, FilterDateButtonProps>(function FilterDateButton(
  {
    label,
    mode = 'single',
    value,
    defaultValue = null,
    onChange,
    format = DEFAULT_FORMAT,
    formatWithoutYear = DEFAULT_FORMAT_WITHOUT_YEAR,
    rangeSeparator = DEFAULT_RANGE_SEPARATOR,
    presets,
    clearable = true,
    clearLabel = 'All dates',
    minDate,
    maxDate,
    disablePast,
    disableFuture,
    shouldDisableDate,
    onOpen,
    onClose,
    onClick,
    id,
    ...rest
  }: FilterDateButtonProps,
  ref
) {
  const generatedId = useId();
  const buttonId = id ?? generatedId;
  const surfaceId = useId();
  const hintId = useId();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const [internalValue, setInternalValue] = useState<FilterDateValue>(defaultValue);
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  // Half-picked range state, live only while the panel is open.
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [preview, setPreview] = useState<Date | null>(null);

  const isRange = mode === 'range';
  const committedRange = asRange(currentValue);
  const committedSingle = asSingle(currentValue);

  const activePresets = presets ?? (isRange ? filterDateRangePresets : []);

  // Reset the half-pick every time the panel opens, so a range abandoned mid-pick can't leak
  // into the next opening.
  useEffect(() => {
    if (open) {
      setDraftStart(null);
      setPreview(null);
    }
  }, [open]);

  const commit = (next: FilterDateValue, event: SyntheticEvent | null) => {
    if (!isControlled) setInternalValue(next);
    onChange?.(event, next);
    setAnchorEl(null);
    onClose?.();
  };

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onOpen?.();
    onClick?.(event);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  // Range clicks: first sets an edge, second completes it. Picking backwards is allowed — the
  // pair is ordered on commit, so the value is always [earlier, later].
  const handleRangeDayPick = (day: Date) => {
    if (draftStart) {
      const ordered: FilterDateRange = isBeforeDay(day, draftStart) ? [day, draftStart] : [draftStart, day];
      setDraftStart(null);
      setPreview(null);
      commit(ordered, null);
      return;
    }
    setDraftStart(day);
    setPreview(null);
  };

  // Which preset (if any) the committed value equals — shown highlighted, and used as the
  // trigger's label because "Last 30 days" reads better than the two dates it stands for.
  const matchedPreset = activePresets.find(preset => {
    const presetValue = preset.getValue();
    if (isRange) {
      const [a, b] = asRange(presetValue);
      return sameDay(a, committedRange[0]) && sameDay(b, committedRange[1]);
    }
    return sameDay(asSingle(presetValue), committedSingle);
  });

  // The literal dates — the trigger falls back to these, and the tooltip always shows them so a
  // preset label never hides what it resolves to.
  const literalValue = (() => {
    if (isRange) {
      const [from, to] = committedRange;
      return from && to
        ? formatFilterDateRange(from, to, {
            format,
            formatWithoutYear,
            separator: rangeSeparator,
          })
        : undefined;
    }
    return committedSingle ? formatDate(committedSingle, format) : undefined;
  })();

  const displayValue = literalValue === undefined ? undefined : (matchedPreset?.label ?? literalValue);
  const valueTooltip = literalValue;

  const showSideColumn = clearable || activePresets.length > 0;

  return (
    <>
      <FilterTrigger
        ref={ref}
        id={buttonId}
        label={label}
        value={displayValue}
        valueTooltip={valueTooltip}
        open={open}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? surfaceId : undefined}
        onClick={handleOpen}
        {...rest}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            // The trigger promises `aria-haspopup="dialog"` — make the surface that dialog,
            // named by the trigger, at the id `aria-controls` points to.
            id: surfaceId,
            role: 'dialog',
            'aria-labelledby': buttonId,
            // Range picking is a two-step gesture nothing in the DOM announces — describe it.
            'aria-describedby': isRange ? hintId : undefined,
            // The paper is absolutely positioned, so only marginTop shifts it: 4px off the
            // trigger, same gap as FilterDropdownPanel.
            sx: { ...paperSx, marginTop: `${spacingTokens.sp4}px` },
          },
        }}
      >
        {isRange && (
          <StyledVisuallyHidden id={hintId}>
            {draftStart ? 'Start date selected. Choose the end date.' : 'Choose the start date, then the end date.'}
          </StyledVisuallyHidden>
        )}

        {showSideColumn && (
          <StyledSideColumn>
            <MenuList role="menu" aria-labelledby={buttonId}>
              {clearable && (
                <MenuItem
                  role="menuitemradio"
                  aria-checked={displayValue === undefined}
                  selected={displayValue === undefined}
                  onClick={event => commit(isRange ? [null, null] : null, event)}
                >
                  {clearLabel}
                </MenuItem>
              )}
              {clearable && activePresets.length > 0 && <Divider />}
              {activePresets.map(preset => (
                <MenuItem
                  key={preset.label}
                  role="menuitemradio"
                  aria-checked={matchedPreset?.label === preset.label}
                  selected={matchedPreset?.label === preset.label}
                  onClick={event => commit(preset.getValue(), event)}
                >
                  {preset.label}
                </MenuItem>
              ))}
            </MenuList>
          </StyledSideColumn>
        )}

        <StyledCalendarColumn
          // Leaving the grid drops the preview, so a stale half-range doesn't stay painted.
          onMouseLeave={() => setPreview(null)}
        >
          {isRange ? (
            <FilterRangeProvider
              value={{
                start: draftStart ?? committedRange[0],
                // While an edge is being re-picked the committed end is irrelevant — the
                // preview stands in for it.
                end: draftStart ? null : committedRange[1],
                preview,
                onDayPick: handleRangeDayPick,
                onDayPreview: setPreview,
              }}
            >
              <DateCalendar
                // Only for the opening month + MUI's internal focus start; the visual selection
                // is decided entirely by FilterRangeDay from the context above.
                value={draftStart ?? committedRange[0]}
                slots={{ day: FilterRangeDay }}
                minDate={minDate}
                maxDate={maxDate}
                disablePast={disablePast}
                disableFuture={disableFuture}
                shouldDisableDate={shouldDisableDate}
              />
            </FilterRangeProvider>
          ) : (
            <DateCalendar
              value={committedSingle}
              // Commit ONLY on a real day pick. With the year→month→day scale, picking a year
              // or a month also fires `onChange` — MUI X reports those as `'partial'`, because
              // there are still views below. Committing one closed the panel on the year click
              // and forced the user to reopen it for the month, and again for the day.
              onChange={(day, selectionState) => {
                if (selectionState === 'finish') commit(day, null);
              }}
              minDate={minDate}
              maxDate={maxDate}
              disablePast={disablePast}
              disableFuture={disableFuture}
              shouldDisableDate={shouldDisableDate}
            />
          )}
        </StyledCalendarColumn>
      </Popover>
    </>
  );
});
